import React, { useState, useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Badge, Menu, MenuItem, Typography, Grid, Slider, Dialog } from '@material-ui/core';
import { makeStyles, createStyles, Theme, useTheme } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ToggleOffIcon from '@material-ui/icons/ToggleOff';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import { VolumeDown, VolumeUp } from '@material-ui/icons';

import { StyledIconButton, SlideTransition } from 'components/shared/MuiExtensions';
import HorizontalContent from 'components/shared/HorizontalContent';
import ConciergeFeedbackReview from './ConciergeFeedbackReview';

import { RootState } from 'state/store';
import { setDarkMode, setSoundEnabled } from 'state/app.state';
import { conciergeLogout, apiSubmitRatingFeedbackResponse } from 'state/concierge.state';
import { setPreference } from 'services/concierge.service';

import { debounce } from 'utils/functions';
import { RatingFeedbackResponse } from 'typings';
import useUnreviewedRatingFeedback, {
  unreviewedRatingFeedbackQueryKey,
} from '../../hooks/useUnreviewedRatingFeedback';
import { useQueryClient } from 'react-query';
import { isArray, isNumber } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      background: theme.palette.primary.main,
      color: 'white',
    },
    sliderStyle: {
      width: 100,
      color: 'white',
    },
  })
);

function ConciergeMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const dispatch = useDispatch();
  const { darkMode, soundEnabled, distributionVolume } = useSelector(
    (state: RootState) => ({
      darkMode: state.app.darkMode,
      soundEnabled: state.app.soundEnabled,
      distributionVolume: state.app.distributionVolume,
    }),
    shallowEqual
  );

  const concierge = useSelector(
    (state: RootState) => ({
      name: state.concierge.entity?.name,
      isActive: state.concierge.isActive,
      isSubmittingFeedbackResponse: state.concierge.isSubmittingFeedbackResponse,
      unreviewedFeedbackCount: state.concierge.entity?.unreviewedFeedback ?? 0,
    }),
    shallowEqual
  );

  const theme = useTheme();
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { data: unreviewedFeedback } = useUnreviewedRatingFeedback();

  // if unreviewedFeedback from UNREVIEWED_RATING_FEEDBACK exists use that because it should always be newer than the ME call
  const [unreviewedFeedbackBadgeCount, setUnreviewedFeedbackBadgeCount] = useState(0);
  useEffect(() => {
    if (isArray(unreviewedFeedback)) {
      setUnreviewedFeedbackBadgeCount(unreviewedFeedback.length);
    } else if (isNumber(concierge.unreviewedFeedbackCount)) {
      setUnreviewedFeedbackBadgeCount(concierge.unreviewedFeedbackCount);
    }
  }, [unreviewedFeedback, concierge.unreviewedFeedbackCount]);

  const handleMenuButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (fn: (...args: any[]) => void) => {
    return (...args: any[]) => {
      fn(...args);
      closeMenu();
    };
  };

  const handleToggleDarkMode = handleMenuItemClick(() => {
    const darkModeSetting = !darkMode;

    dispatch(setDarkMode(darkModeSetting));
    setPreference('darkMode', darkModeSetting);
  });

  // toggles soundEnabled boolean state
  const handleToggleSoundEnabled = () => {
    const soundEnabledSetting = !soundEnabled;

    dispatch(setSoundEnabled(soundEnabledSetting));
    setPreference('soundEnabled', soundEnabledSetting);
    // Prevents menu from closing before user can adjust volume
    // Previously closes menu as user turns sound on
    if (soundEnabled) {
      closeMenu();
    }
  };

  const updateDistributionVolume = (distributionVolume: number | number[]) => {
    setPreference('distributionVolume', distributionVolume);
  };

  const distributionVolumeHandler = useCallback(debounce(updateDistributionVolume, 500), []);

  const handleVolumeChange = (event: any, distributionVolume: number | number[]) => {
    distributionVolumeHandler(distributionVolume);
  };

  const handleConciergeLogout = handleMenuItemClick(() => {
    dispatch(conciergeLogout(true));
  });

  const handleToggleFeedbackReviewModal = handleMenuItemClick(() => {
    if (!concierge.isActive) {
      setShowFeedback(!showFeedback);
    }
  });

  const handleSubmitFeedbackResponse = async (feedbackResponse: RatingFeedbackResponse) => {
    await dispatch(apiSubmitRatingFeedbackResponse(feedbackResponse));
    queryClient.invalidateQueries(unreviewedRatingFeedbackQueryKey);
  };

  return (
    <Badge
      component="div"
      badgeContent={unreviewedFeedbackBadgeCount}
      color="error"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <StyledIconButton onClick={handleMenuButtonClick} color={theme.palette.text.primary}>
        <Typography variant="caption">{concierge.name}</Typography>
        <KeyboardArrowDownIcon />
      </StyledIconButton>
      <Menu
        id="concierge-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        classes={{ paper: classes.paper }}
      >
        <MenuItem onClick={handleToggleDarkMode}>
          <HorizontalContent>
            {darkMode ? <ToggleOnIcon /> : <ToggleOffIcon />}
            <Typography variant="subtitle1">{darkMode ? 'light mode' : 'dark mode'}</Typography>
          </HorizontalContent>
        </MenuItem>
        <MenuItem onClick={handleToggleSoundEnabled}>
          <HorizontalContent>
            {soundEnabled ? <ToggleOnIcon /> : <ToggleOffIcon />}
            <Typography variant="subtitle1">{soundEnabled ? 'sound off' : 'sound on'}</Typography>
          </HorizontalContent>
        </MenuItem>
        {soundEnabled && (
          <MenuItem>
            <Grid item xs={10} sm={10} md={3}>
              <VolumeDown />
            </Grid>
            <Grid item xs>
              <Slider
                defaultValue={distributionVolume}
                onChange={handleVolumeChange}
                onMouseUp={closeMenu}
                min={0}
                step={0.002}
                max={1}
                aria-labelledby="continuous-slider"
                valueLabelDisplay="auto"
                className={classes.sliderStyle}
              />
            </Grid>
            <Grid item xs={10} sm={10} md={3}>
              <VolumeUp />
            </Grid>
          </MenuItem>
        )}
        {!concierge.isActive && !!unreviewedFeedback?.length && (
          <MenuItem onClick={handleToggleFeedbackReviewModal}>
            <HorizontalContent>
              <Typography variant="subtitle1">Review Feedback</Typography>
            </HorizontalContent>
          </MenuItem>
        )}
        <MenuItem onClick={handleConciergeLogout}>
          <HorizontalContent>
            <ExitToAppIcon />
            <Typography variant="subtitle1">logout</Typography>
          </HorizontalContent>
        </MenuItem>
      </Menu>
      {!!unreviewedFeedback?.length && (
        <Dialog
          fullWidth={true}
          maxWidth="lg"
          open={showFeedback}
          onClose={handleToggleFeedbackReviewModal}
          TransitionComponent={SlideTransition}
          aria-labelledby="feedback-review-dialog"
          scroll="paper"
        >
          <ConciergeFeedbackReview
            rating={unreviewedFeedback}
            onFeedbackResponse={handleSubmitFeedbackResponse}
            disableDoneAction={concierge.isSubmittingFeedbackResponse}
          />
        </Dialog>
      )}
    </Badge>
  );
}

export default ConciergeMenu;
